import { inject, Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { catchError, map, of, switchMap } from "rxjs";
import { SnackbarService } from "../../core/services";
import { CalendarIntegrationApi } from "./calendar-integration.api";
import * as ActionsSet from "./calendar-integration.actions";

@Injectable()
export class CalendarIntegrationEffects {
  private readonly actions$ = inject(Actions);
  private readonly api = inject(CalendarIntegrationApi);
  private readonly snackbar = inject(SnackbarService);

  fetch$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ActionsSet.CalendarIntegrationFetchRequest),
      switchMap(() =>
        this.api.list().pipe(
          map((response) => ActionsSet.CalendarIntegrationFetchSuccess({ integrations: response.data })),
          catchError((error) => of(ActionsSet.CalendarIntegrationFetchFailure({ error: error?.message ?? error }))),
        ),
      ),
    ),
  );

  upsert$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ActionsSet.CalendarIntegrationUpsertRequest),
      switchMap(({ payload }) =>
        this.api.upsert(payload).pipe(
          map((response) => ActionsSet.CalendarIntegrationUpsertSuccess({ integration: response.data })),
          catchError((error) => of(ActionsSet.CalendarIntegrationUpsertFailure({ error: error?.message ?? error }))),
        ),
      ),
    ),
  );

  disconnect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ActionsSet.CalendarIntegrationDisconnectRequest),
      switchMap(({ provider }) =>
        this.api.disconnect(provider).pipe(
          map(() => ActionsSet.CalendarIntegrationDisconnectSuccess({ provider })),
          catchError((error) => of(ActionsSet.CalendarIntegrationDisconnectFailure({ error: error?.message ?? error }))),
        ),
      ),
    ),
  );

  successRefresh$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ActionsSet.CalendarIntegrationUpsertSuccess, ActionsSet.CalendarIntegrationDisconnectSuccess),
      map(() => ActionsSet.CalendarIntegrationFetchRequest()),
    ),
  );

  notifications$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ActionsSet.CalendarIntegrationUpsertSuccess, ActionsSet.CalendarIntegrationDisconnectSuccess),
        map((action) => {
          const message = action.type === ActionsSet.CalendarIntegrationDisconnectSuccess.type ? 'Calendar integration disconnected.' : 'Calendar integration saved.';
          this.snackbar.showNotification('success', '', message, 'Close');
        }),
      ),
    { dispatch: false },
  );

  failures$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          ActionsSet.CalendarIntegrationFetchFailure,
          ActionsSet.CalendarIntegrationUpsertFailure,
          ActionsSet.CalendarIntegrationDisconnectFailure,
        ),
        map(({ error }) => {
          this.snackbar.showNotification('error', '', `Calendar integration error: ${error}`, 'Close');
        }),
      ),
    { dispatch: false },
  );
}

